import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTimes,
  FaQuestionCircle,
  FaUser,
  FaTag,
  FaCheck,
  FaBuilding,
  FaEnvelope,
  FaPhone,
} from "react-icons/fa";
import CommonModal from "./CommonModal";
import { catchError, commonModalSetting } from "JS/common";
import { postAdminInquiry } from "JS/API";
import { designTokens, commonStyles } from "utils/designSystem";
import { detailModalStyles } from "utils/modalDesignSystem";

const INQUIRY_TYPES = [
  "등록가능성 문의",
  "상표등록신청",
  "상표문의",
  "기타(서비스 불편사항)",
];

const INQUIRY_REASONS = [
  "박랍회",
  "인터넷 검색",
  "인스타그램",
  "블로그",
  "유튜브",
  "카톡채널",
  "뉴스 및 보도자료",
  "지인 추천",
  "이메일 뉴스레터",
  "기타",
];

const APPLY_TYPES = [
  "일반출원대행",
  "조사출원대행",
  "신속출원대행",
  "신속조사출원대행",
];

const INITIAL_FORM = {
  display_as_member: true,
  author_name: "",
  inquiry_type: "등록가능성 문의",
  title: "",
  context: "",
  apply_type: "",
  applicant_type: "",
  inquiry_reason: "",
  mark_name: "",
  guest_email: "",
  guest_phone: "",
};

const InquiryCreateModal = ({ setModal, onSuccess }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const navigate = useNavigate();

  const update = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length < 4) return digits;
    if (digits.length < 7)
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    if (digits.length < 11)
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  };

  const styles = {
    overlay: detailModalStyles.overlay,
    container: detailModalStyles.container,
    header: {
      padding: designTokens.spacing.xxl,
      borderBottom: `1px solid ${designTokens.colors.border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    title: detailModalStyles.title,
    closeButton: {
      ...commonStyles.buttonBase,
      padding: designTokens.spacing.md,
      borderRadius: "50%",
      backgroundColor: "transparent",
      color: designTokens.colors.text.tertiary,
      border: "none",
      width: "36px",
      height: "36px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    },
    content: detailModalStyles.content,
    section: detailModalStyles.section,
    sectionTitle: {
      display: "flex",
      alignItems: "center",
      gap: designTokens.spacing.md,
      fontSize: "15px",
      fontWeight: "600",
      color: designTokens.colors.text.primary,
      marginBottom: designTokens.spacing.lg,
      paddingBottom: designTokens.spacing.md,
      borderBottom: `2px solid ${designTokens.colors.primary}`,
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      gap: designTokens.spacing.lg,
    },
    field: {
      display: "flex",
      flexDirection: "column",
      gap: designTokens.spacing.sm,
    },
    label: {
      fontSize: "13px",
      fontWeight: "600",
      color: designTokens.colors.text.secondary,
      display: "flex",
      alignItems: "center",
      gap: designTokens.spacing.sm,
    },
    required: {
      color: "#dc2626",
      marginLeft: "2px",
    },
    input: {
      padding: `${designTokens.spacing.md} ${designTokens.spacing.md}`,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      fontSize: "14px",
      fontFamily: "inherit",
      backgroundColor: designTokens.colors.surface,
    },
    textarea: {
      padding: designTokens.spacing.md,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      fontSize: "14px",
      fontFamily: "inherit",
      minHeight: "160px",
      resize: "vertical",
      lineHeight: "1.6",
    },
    radioGroup: {
      display: "flex",
      gap: designTokens.spacing.md,
      flexWrap: "wrap",
    },
    radioOption: {
      display: "flex",
      alignItems: "center",
      gap: designTokens.spacing.sm,
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.lg}`,
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.borderLight}`,
      backgroundColor: designTokens.colors.surface,
      fontSize: "13px",
      cursor: "pointer",
      userSelect: "none",
    },
    radioOptionSelected: {
      backgroundColor: designTokens.colors.primary,
      color: designTokens.colors.surface,
      borderColor: designTokens.colors.primary,
    },
    footer: {
      padding: designTokens.spacing.xl,
      borderTop: `1px solid ${designTokens.colors.border}`,
      display: "flex",
      justifyContent: "flex-end",
      gap: designTokens.spacing.md,
      backgroundColor: designTokens.colors.background,
    },
    primaryButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.primary,
      color: designTokens.colors.surface,
      padding: `${designTokens.spacing.md} ${designTokens.spacing.xxl}`,
      fontSize: "14px",
      fontWeight: "600",
      cursor: submitting ? "not-allowed" : "pointer",
      opacity: submitting ? 0.6 : 1,
    },
    secondaryButton: detailModalStyles.secondaryButton,
  };

  const radio = (value, checked, onClick, icon) => (
    <div
      style={{
        ...styles.radioOption,
        ...(checked ? styles.radioOptionSelected : {}),
      }}
      onClick={onClick}
    >
      {icon}
      <span>{value}</span>
    </div>
  );

  const validate = () => {
    if (!form.author_name.trim()) return "작성자 이름을 입력해 주세요.";
    if (form.author_name.length > 20)
      return "작성자 이름은 20자 이내로 입력해 주세요.";
    if (!form.inquiry_type) return "문의 유형을 선택해 주세요.";
    if (!form.title.trim()) return "제목을 입력해 주세요.";
    if (form.title.length > 300) return "제목은 300자 이내로 입력해 주세요.";
    if (!form.context.trim()) return "내용을 입력해 주세요.";
    if (form.context.length > 3000)
      return "내용은 3000자 이내로 입력해 주세요.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      return commonModalSetting(setAlertBox, true, "alert", err);
    }
    setSubmitting(true);

    const body = {
      display_as_member: form.display_as_member,
      author_name: form.author_name.trim(),
      inquiry_type: form.inquiry_type,
      title: form.title.trim(),
      context: form.context,
    };
    if (form.inquiry_reason.trim())
      body.inquiry_reason = form.inquiry_reason.trim();
    if (form.mark_name.trim()) body.mark_name = form.mark_name.trim();
    if (form.inquiry_type === "상표등록신청") {
      if (form.apply_type) body.apply_type = form.apply_type;
      if (form.applicant_type) body.applicant_type = form.applicant_type;
    }
    if (!form.display_as_member) {
      if (form.guest_email.trim()) body.guest_email = form.guest_email.trim();
      if (form.guest_phone.trim()) body.guest_phone = form.guest_phone.trim();
    }

    const result = await postAdminInquiry(body);
    setSubmitting(false);
    if (typeof result === "object") {
      setAlert("createComplete");
      commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "임의 문의글이 정상적으로 등록되었습니다."
      );
    } else {
      return await catchError(result, navigate, setAlertBox, setAlert);
    }
  };

  return (
    <>
      <div style={styles.overlay}>
        <div
          style={styles.container}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={styles.header}>
            <h2 style={styles.title}>
              <FaQuestionCircle />
              임의 문의글 작성
            </h2>
            <button
              style={styles.closeButton}
              onClick={() => setModal(false)}
            >
              <FaTimes size={16} />
            </button>
          </div>

          <div style={styles.content}>
            {/* 표시 방식 */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <FaUser />
                표시 방식
              </h3>
              <div style={styles.radioGroup}>
                {radio(
                  "회원 글로 표시",
                  form.display_as_member === true,
                  () => update("display_as_member", true),
                  <FaUser size={12} />
                )}
                {radio(
                  "비회원 글로 표시",
                  form.display_as_member === false,
                  () => update("display_as_member", false),
                  <FaUser size={12} />
                )}
              </div>
            </div>

            {/* 기본 정보 */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <FaTag />
                기본 정보
              </h3>
              <div style={styles.grid}>
                <div style={styles.field}>
                  <label style={styles.label}>
                    작성자 이름<span style={styles.required}>*</span>
                  </label>
                  <input
                    style={styles.input}
                    type="text"
                    maxLength={20}
                    value={form.author_name}
                    onChange={(e) => update("author_name", e.target.value)}
                    placeholder="목록에 표시될 이름"
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>
                    문의 유형<span style={styles.required}>*</span>
                  </label>
                  <select
                    style={styles.input}
                    value={form.inquiry_type}
                    onChange={(e) => update("inquiry_type", e.target.value)}
                  >
                    {INQUIRY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>상표명</label>
                  <input
                    style={styles.input}
                    type="text"
                    maxLength={255}
                    value={form.mark_name}
                    onChange={(e) => update("mark_name", e.target.value)}
                    placeholder="문의 상표 이름 (선택)"
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>유입 경로</label>
                  <select
                    style={styles.input}
                    value={form.inquiry_reason}
                    onChange={(e) => update("inquiry_reason", e.target.value)}
                  >
                    <option value="">선택 안 함 (관리자 작성)</option>
                    {INQUIRY_REASONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* 상표등록신청 전용 */}
            {form.inquiry_type === "상표등록신청" && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                  <FaBuilding />
                  상표등록 상세
                </h3>
                <div
                  style={{
                    ...styles.field,
                    marginBottom: designTokens.spacing.lg,
                  }}
                >
                  <label style={styles.label}>출원인 유형</label>
                  <div style={styles.radioGroup}>
                    {radio(
                      "개인",
                      form.applicant_type === "개인",
                      () => update("applicant_type", "개인"),
                      <FaUser size={12} />
                    )}
                    {radio(
                      "법인",
                      form.applicant_type === "법인",
                      () => update("applicant_type", "법인"),
                      <FaBuilding size={12} />
                    )}
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>출원 유형</label>
                  <div style={styles.radioGroup}>
                    {APPLY_TYPES.map((t) =>
                      radio(
                        t,
                        form.apply_type === t,
                        () => update("apply_type", t),
                        null
                      )
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 비회원 연락처 */}
            {form.display_as_member === false && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                  <FaUser />
                  비회원 연락처
                </h3>
                <div style={styles.grid}>
                  <div style={styles.field}>
                    <label style={styles.label}>
                      <FaEnvelope size={12} />
                      이메일
                    </label>
                    <input
                      style={styles.input}
                      type="email"
                      maxLength={100}
                      value={form.guest_email}
                      onChange={(e) => update("guest_email", e.target.value)}
                      placeholder="example@test.com"
                    />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>
                      <FaPhone size={12} />
                      연락처
                    </label>
                    <input
                      style={styles.input}
                      type="text"
                      maxLength={13}
                      value={form.guest_phone}
                      onChange={(e) =>
                        update("guest_phone", formatPhone(e.target.value))
                      }
                      placeholder="010-0000-0000"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 제목 / 내용 */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <FaQuestionCircle />
                문의 내용
              </h3>
              <div style={styles.field}>
                <label style={styles.label}>
                  제목<span style={styles.required}>*</span>
                </label>
                <input
                  style={styles.input}
                  type="text"
                  maxLength={300}
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  placeholder="제목을 입력해 주세요"
                />
              </div>
              <div
                style={{
                  ...styles.field,
                  marginTop: designTokens.spacing.lg,
                }}
              >
                <label style={styles.label}>
                  내용<span style={styles.required}>*</span>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: "12px",
                      color: designTokens.colors.text.tertiary,
                      fontWeight: "400",
                    }}
                  >
                    {form.context.length} / 3000
                  </span>
                </label>
                <textarea
                  style={styles.textarea}
                  maxLength={3000}
                  value={form.context}
                  onChange={(e) => update("context", e.target.value)}
                  placeholder="문의 내용을 입력해 주세요"
                />
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            <button
              style={styles.secondaryButton}
              onClick={() => setModal(false)}
              disabled={submitting}
            >
              취소
            </button>
            <button
              style={styles.primaryButton}
              onClick={submit}
              disabled={submitting}
            >
              <FaCheck size={12} />
              {submitting ? "등록 중..." : "등록"}
            </button>
          </div>
        </div>
      </div>

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "createComplete") {
              if (onSuccess) onSuccess();
              setModal(false);
            } else if (alert === "logout") navigate("/");
          }}
        />
      )}
    </>
  );
};

export default InquiryCreateModal;
